import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Image, Inscription, Preloader, Text, useScrollByDrag } from '@common-fe/common-fe';
import _ from 'lodash';

import Download from '@/assets/pdfPreview/Download.svg';
import RotateLeft from '@/assets/pdfPreview/RotateLeft.svg';
import RotateRight from '@/assets/pdfPreview/RotateRight.svg';
import { Document, DocumentTypeEnum } from '@/common/types';
import {
  documentFiles,
  RenderWrapperProps,
  ROTATE_STEP_DEG,
  textFiles,
} from '@/components/elements/DocumentViewer/DocumentViewer.constants';
import {
  ActionButton,
  LeftActionsWrap,
  Minus,
  Plus,
  RightActionsWrap,
  SectionWrap,
  StyledAppealBadge,
  StyledFooter,
  StyledPreviewBox,
} from '@/components/elements/DocumentViewer/DocumentViewer.styles';
import { getFileFromDocumentById } from '@/components/elements/DocumentViewer/queries/useGetDocumentFiles.query';
import { useConvertToLMN } from '@/modules/claims/ClaimsProcessor/ClaimConvertToLMN/hooks';
import colors from '@/styles/colors';

import { CsvViewer, ImageViewer, PdfViewer, TiffViewer, TxtViewer } from './components';
import IconsPreview from './IconsPreview';

const MIN_ROTATE_VALUE = 0;
const MAX_ROTATE_VALUE = 270;
const INIT_ZOOM = 1;
const MINIMUM_ZOOM_SIZE = 0.2;
const INIT_GRID_ZOOM = 0.9;
const MINIMUM_GRID_ZOOM_SIZE = 0.9;
const MAXIMUM_ZOOM_SIZE = 2.0;

export interface Props {
  receipts: Document[];
  isLoading?: boolean;
  renderWrapper?: (props: RenderWrapperProps) => React.ReactNode;
}

const DocumentViewer: React.FC<Props> = ({ receipts, isLoading, renderWrapper }) => {
  const [zoom, setZoom] = useState(1);

  const [isMultiPageViewMode, setIsMultiPageViewMode] = useState(false);
  const [rotate, setRotate] = useState(MIN_ROTATE_VALUE);
  const [activePage, setActivePage] = useState(0);
  const scrollWrapRef = useRef<HTMLDivElement>(null);
  const [isRotationDisabled, setIsRotationDisabled] = useState(false);
  const [isZoomDisabled, setIsZoomDisabled] = useState(false);
  const { onMouseDown, onMouseUp, onMouseMove, onMouseLeave, isScrolling } = useScrollByDrag(scrollWrapRef);
  const activeReceiptId = useMemo(() => receipts[activePage]?.id, [receipts, activePage]);
  const activeReceipt = useMemo(() => receipts[activePage], [receipts, activePage]);

  useConvertToLMN({
    file: activeReceipt,
  });
  const pdfReceipts = useMemo(
    () => receipts.filter((receipt) => receipt.type === DocumentTypeEnum.PDF),
    [receipts]
  );
  const activePdfReceipt = useMemo(
    () => pdfReceipts.find((receipt) => receipt.id === activeReceiptId),
    [pdfReceipts, activeReceiptId]
  );
  const imageReceipts = useMemo(
    () => receipts.filter((receipt) => !documentFiles.has(receipt.type) && !textFiles.has(receipt.type)),
    [receipts]
  );
  const activeImageReceipt = useMemo(
    () => imageReceipts.find((receipt) => receipt.id === activeReceiptId),
    [imageReceipts, activeReceiptId]
  );

  const tifReceipts = useMemo(
    () => receipts.filter((receipt) => receipt.type === DocumentTypeEnum.TIF),
    [receipts]
  );
  const activeTifReceipt = useMemo(
    () => tifReceipts.find((receipt) => receipt.id === activeReceiptId),
    [tifReceipts, activeReceiptId]
  );

  const textReceipts = useMemo(
    () => receipts.filter((receipt) => receipt.type === DocumentTypeEnum.TXT),
    [receipts]
  );
  const activeTextReceipt = useMemo(
    () => textReceipts.find((receipt) => receipt.id === activeReceiptId),
    [textReceipts, activeReceiptId]
  );

  const csvReceipts = useMemo(
    () => receipts.filter((receipt) => receipt.type === DocumentTypeEnum.CSV),
    [receipts]
  );
  const activeCsvReceipt = useMemo(
    () => csvReceipts.find((receipt) => receipt.id === activeReceiptId),
    [csvReceipts, activeReceiptId]
  );

  const isRotateAvailable = useMemo(
    () => !textFiles.has(activeReceipt?.type) && !isRotationDisabled,
    [activeReceipt, isRotationDisabled]
  );
  const isZoomAvailable = useMemo(() => !isZoomDisabled, [isZoomDisabled]);

  useLayoutEffect(() => {
    if (isMultiPageViewMode) {
      setZoom(INIT_GRID_ZOOM);
      return;
    }
    setZoom(INIT_ZOOM);
  }, [activePage, isMultiPageViewMode]);

  useEffect(() => {
    if (activePage > 0 && isLoading) {
      setActivePage(0);
    }
  }, [isLoading, activePage]);

  const handleDownloadFile = useCallback(async () => {
    const receipt = receipts[activePage];

    if (receipt?.isAppeal) {
      if (receipt?.link) {
        window.open(receipt.link);
      }
      return;
    }

    const documentId = receipt?.documentId;
    const fileId = receipt?.fileId;

    if (!documentId || !fileId) {
      return;
    }

    const file = await getFileFromDocumentById(documentId, fileId);
    if (file?.s3_url) {
      window.open(file?.s3_url);
    }
  }, [receipts, activePage]);

  return (
    <SectionWrap data-testid="document-viewer_wrapper" direction="column" background="module">
      <SectionWrap>
        <SectionWrap
          overflow="auto"
          direction="column"
          pad="spacing12"
          ref={scrollWrapRef}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
          style={{ cursor: isScrolling ? 'grabbing' : 'grab' }}
        >
          <Box direction="column" height={{ min: 'auto' }} flex="grow">
            <Box direction="column" round="container1Round" flex="grow" style={{ position: 'relative' }}>
              {activeReceipt?.isAppeal && (
                <StyledAppealBadge round="xsmall" align="center" justify="center">
                  <Inscription weight="normal" color="textBody">Appeal</Inscription>
                </StyledAppealBadge>
              )}
              {isLoading ? (
                <Box
                  background="canvas"
                  round="container1Round"
                  margin={{ right: 'spacing12' }}
                  height="440px"
                  align="center"
                  justify="center"
                >
                  <Preloader testId="document-viewer-preloader" />
                </Box>
              ) : (
                <>
                  {Boolean(activePdfReceipt?.id) && (
                    <Box data-testid="pdf-preview_document-viewer" width="100%" height="100%">
                      <StyledPreviewBox
                        key={activePdfReceipt?.id}
                        align="center"
                        margin={{ bottom: 'spacing6' }}
                        width="inherit"
                        height="inherit"
                      >
                        <PdfViewer
                          receipt={activePdfReceipt}
                          zoom={zoom}
                          rotate={rotate}
                          renderWrapper={renderWrapper}
                          isMultiPageViewMode={isMultiPageViewMode}
                          setIsMultiPageViewMode={setIsMultiPageViewMode}
                          setIsRotationDisabled={setIsRotationDisabled}
                          setIsZoomDisabled={setIsZoomDisabled}
                        />
                      </StyledPreviewBox>
                    </Box>
                  )}
                  {Boolean(activeImageReceipt?.id) && (
                    <Box
                      data-testid={`preview-image_${activeImageReceipt?.id}`}
                      align="center"
                      style={{ display: 'block' }}
                    >
                      <ImageViewer
                        zoom={zoom}
                        key={activeImageReceipt?.id}
                        rotate={rotate}
                        receipt={activeImageReceipt as Document}
                        margin={{ bottom: 'spacing12' }}
                      />
                    </Box>
                  )}
                  {Boolean(activeTifReceipt?.id) && (
                    <Box data-testid={`preview-tiff_${activeTifReceipt?.id}`}>
                      <TiffViewer
                        receipt={activeTifReceipt}
                        zoom={zoom}
                        rotate={rotate}
                        renderWrapper={renderWrapper}
                        isMultiPageViewMode={isMultiPageViewMode}
                        setIsMultiPageViewMode={setIsMultiPageViewMode}
                      />
                    </Box>
                  )}
                  {Boolean(activeTextReceipt?.id) && (
                    <Box data-testid={`preview-txt_${activeTextReceipt?.id}`}>
                      <TxtViewer receipt={activeTextReceipt} zoom={zoom} />
                    </Box>
                  )}
                  {Boolean(activeCsvReceipt?.id) && (
                    <Box data-testid={`preview-csv_${activeCsvReceipt?.id}`}>
                      <CsvViewer receipt={activeCsvReceipt} zoom={zoom} />
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        </SectionWrap>
        <StyledFooter
          pad="spacing8"
          direction="row"
          align="center"
          justify="center"
          background={colors.background1}
        >
          {receipts.length > 0 && (
            <LeftActionsWrap
              isTop={receipts.length > 1}
              pad={{ horizontal: '7px', vertical: '2px' }}
              round="smallRound"
            >
              <Text color={receipts.length > 1 ? 'textOnColor' : 'textSecondary'} size="xsmall">
                {activePage + 1} / {receipts.length}
              </Text>
            </LeftActionsWrap>
          )}
          {receipts.length > 1 && (
            <IconsPreview
              activeDocumentId={activeReceiptId}
              documents={receipts}
              activateDocument={(index: number) => {
                setActivePage(index);
                setRotate(MIN_ROTATE_VALUE);
              }}
            />
          )}
          {isZoomAvailable && (
            <Box direction="row">
              <ActionButton
                data-testid="zoom-button_document-viewer"
                onClick={() =>
                  setZoom((currentZoom) => {
                    const updatedZoom = _.round(currentZoom + 0.1, 2);
                    return updatedZoom >= MAXIMUM_ZOOM_SIZE ? MAXIMUM_ZOOM_SIZE : updatedZoom;
                  })}
                round="checkboxRound"
                width="40px"
                height="40px"
                align="center"
                justify="center"
                margin={{ right: 'spacing4' }}
              >
                <Plus />
              </ActionButton>
              <ActionButton
                data-testid="zoom-out-button_document-viewer"
                onClick={() =>
                  setZoom((currentZoom) => {
                    const updatedZoom = _.round(currentZoom - 0.1, 2);
                    if (isMultiPageViewMode) {
                      return updatedZoom <= MINIMUM_GRID_ZOOM_SIZE ? MINIMUM_GRID_ZOOM_SIZE : updatedZoom;
                    }
                    return updatedZoom <= MINIMUM_ZOOM_SIZE ? MINIMUM_ZOOM_SIZE : updatedZoom;
                  })}
                round="checkboxRound"
                width="40px"
                height="40px"
                align="center"
                justify="center"
              >
                <Minus />
              </ActionButton>
            </Box>
          )}
          <RightActionsWrap direction="row">
            {isRotateAvailable && (
              <>
                <ActionButton
                  data-testid="rotate-left-button_document-viewer"
                  onClick={() =>
                    setRotate(rotate === MIN_ROTATE_VALUE ? MAX_ROTATE_VALUE : rotate - ROTATE_STEP_DEG)}
                  round="checkboxRound"
                  width="40px"
                  height="40px"
                  align="center"
                  justify="center"
                  margin={{ right: 'spacing4' }}
                >
                  <Image height="24px" src={RotateLeft} alt="icon" opacity={isLoading && 'weak'} />
                </ActionButton>
                <ActionButton
                  data-testid="rotate-right-button_document-viewer"
                  onClick={() =>
                    setRotate(rotate === MAX_ROTATE_VALUE ? MIN_ROTATE_VALUE : rotate + ROTATE_STEP_DEG)}
                  round="checkboxRound"
                  width="40px"
                  height="40px"
                  align="center"
                  justify="center"
                  margin={{ right: 'spacing8' }}
                >
                  <Image height="24px" src={RotateRight} alt="icon" opacity={isLoading && 'weak'} />
                </ActionButton>
              </>
            )}
            <Box height="40px" width="1px" margin={{ right: 'spacing8' }} />
            <ActionButton
              data-testid="download-button_document-viewer"
              onClick={handleDownloadFile}
              round="checkboxRound"
              width="40px"
              height="40px"
              align="center"
              justify="center"
            >
              <Image height="24px" src={Download} alt="icon" opacity={isLoading && 'weak'} />
            </ActionButton>
          </RightActionsWrap>
        </StyledFooter>
      </SectionWrap>
    </SectionWrap>
  );
};

export default DocumentViewer;
